import { useAuth0 } from "@auth0/auth0-react";
import { Capacitor } from "@capacitor/core";
import { useCallback, useRef } from "react";
import { useSessionTimeout } from "../../hooks/useSessionTimeout";
import { useAnalytics } from "../../lib/analytics";
import { performLogout } from "../../lib/auth-logout";
import { SessionTimeoutWarningModal } from "./SessionTimeoutWarningModal";

// Idle-session timeout (15-min warning → auto-logout) can be turned off via
// VITE_DISABLE_SESSION_TIMEOUT — useful for testers/demos who don't want to be
// logged out mid-session. DEFAULT (var unset) keeps the timeout ACTIVE; any
// truthy value ("true"/"1"/etc.) disables it. An explicit "false"/"0"/empty
// re-enables it.
// Normalize (trim + lowercase) so "False", " false ", etc. behave as expected.
const flag = import.meta.env.VITE_DISABLE_SESSION_TIMEOUT?.trim().toLowerCase();
const SESSION_TIMEOUT_DISABLED =
	flag !== undefined && flag !== "" && flag !== "false" && flag !== "0";

export function SessionTimeoutHandler() {
	const { isAuthenticated, logout, getAccessTokenSilently } = useAuth0();
	const analytics = useAnalytics();
	const analyticsRef = useRef(analytics);
	analyticsRef.current = analytics;

	const logoutRef = useRef(logout);
	logoutRef.current = logout;

	const getTokenRef = useRef(getAccessTokenSilently);
	getTokenRef.current = getAccessTokenSilently;

	const handleWarning = useCallback(() => {
		analyticsRef.current.logAuth("session_timeout_warning_shown", {
			platform: Capacitor.getPlatform(),
		});
	}, []);

	const handleTimeout = useCallback(() => {
		analyticsRef.current.logAuth("logout", {
			trigger: "session_timeout",
			platform: Capacitor.getPlatform(),
		});
		performLogout(logoutRef.current);
	}, []);

	const { isWarning, countdown, resetTimer } = useSessionTimeout({
		onWarning: handleWarning,
		onTimeout: handleTimeout,
		enabled: isAuthenticated && !SESSION_TIMEOUT_DISABLED,
	});

	const handleStayLoggedIn = useCallback(() => {
		analyticsRef.current.logAuth("session_timeout_stay_logged_in", {
			platform: Capacitor.getPlatform(),
		});
		resetTimer();

		// Proactively refresh token to prevent stale token after idle period
		getTokenRef
			.current({
				authorizationParams: {
					audience: import.meta.env.VITE_AUTH0_AUDIENCE,
				},
				cacheMode: "off",
			})
			.catch((error) => {
				console.error("Failed to refresh token on stay logged in:", error);
			});
	}, [resetTimer]);

	const handleLogOut = useCallback(() => {
		analyticsRef.current.logAuth("logout", {
			trigger: "session_timeout_manual",
			platform: Capacitor.getPlatform(),
		});
		analyticsRef.current.logAuth("session_timeout_manual_logout", {
			platform: Capacitor.getPlatform(),
		});
		performLogout(logoutRef.current);
	}, []);

	if (!isWarning) return null;

	return (
		<SessionTimeoutWarningModal
			countdown={countdown}
			onStayLoggedIn={handleStayLoggedIn}
			onLogOut={handleLogOut}
		/>
	);
}
